import { Grid } from '@mui/material';
import { set } from 'lodash';
import { type Path } from 'react-hook-form';
import {
  ColorPickerWidget,
  InputWidget,
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { FONT_FAMILY_OPTIONS } from 'src/constants';
import type { SubHeaderDescriptionConfig } from 'src/features/components/editComponentPage/forms/FormSelector.interface';
import { useGetProjectConfig } from 'src/hooks';
import { lang } from 'src/utils';

import { CategoryItemDescriptionProps } from './CategoryItemDescription.interface';

export const CategoryItemDescription = (
  props: CategoryItemDescriptionProps,
) => {
  const { data: projectConfig } = useGetProjectConfig();

  const projectFonts =
    projectConfig?.fonts?.map((item) => ({
      label: item.name,
      value: item.name,
    })) ?? [];
  const mappedFonts =
    projectFonts.length > 0 ? projectFonts : FONT_FAMILY_OPTIONS;

  const handleFieldChange =
    (field: Path<CategoryItemDescriptionProps['value']>) => (value: any) => {
      props.handleDescriptionChange(
        set({ ...props.value } as SubHeaderDescriptionConfig, field, value),
      );
    };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SwitchButtonWidget
          label={lang.subheaderDescription}
          value={props.value?.visible}
          onChange={handleFieldChange('visible')}
        />
      </Grid>

      {props.value?.visible && (
        <>
          <Grid data-testid="subheader-description-text-grid" item xs={12}>
            <InputWidget
              label={lang.descriptionText}
              value={props.value?.text}
              onChange={handleFieldChange('text')}
            />
          </Grid>
          <Grid data-testid="subheader-font-family-grid" item xs={6}>
            <SelectDropdownWidget
              selectOptions={mappedFonts}
              value={
                props?.value?.font ||
                props?.subHeaderDescriptionComponentConfig?.font ||
                props.theme?.config.subheaderDescription?.font
              }
              label={lang.fontFamily}
              onChange={handleFieldChange('font')}
            />
          </Grid>
          <Grid data-testid="subheader-font-size-grid" item xs={6}>
            <NumberInputWidget
              label={lang.fontSize}
              value={
                props.value?.fontSize ??
                props.subHeaderDescriptionComponentConfig?.fontSize ??
                props.theme?.config.subheaderDescription?.fontSize
              }
              onChange={handleFieldChange('fontSize')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid data-testid="subheader-color-picker-grid" item xs={6}>
            <ColorPickerWidget
              label={lang.fontColor}
              value={
                props.value?.color ??
                props.subHeaderDescriptionComponentConfig?.color ??
                props.theme?.config.subheaderDescription?.color
              }
              colorPickerMode="block"
              pickerWrapperStyle={{ left: '0' }}
              options={{ triangle: 'hide' }}
              onChange={handleFieldChange('color')}
            />
          </Grid>
          <Grid data-testid="subheader-line-height-grid" item xs={6}>
            <NumberInputWidget
              label={lang.lineHeight}
              value={
                props.value?.lineHeight ??
                props.subHeaderDescriptionComponentConfig?.lineHeight ??
                props.theme?.config.subheaderDescription?.lineHeight
              }
              onChange={handleFieldChange('lineHeight')}
              options={{ suffix: 'px' }}
            />
          </Grid>

          <Grid data-testid="subheader-margin-below-grid" item xs={6}>
            <NumberInputWidget
              label={lang.marginBelow}
              value={
                props?.value?.marginBottom ??
                props.subHeaderDescriptionComponentConfig?.marginBottom
              }
              onChange={handleFieldChange('marginBottom')}
              options={{ suffix: 'px' }}
            />
          </Grid>
          <Grid data-testid="subheader-height-grid" item xs={6}>
            <NumberInputWidget
              label={lang.height}
              value={
                props.value.height ??
                props.subHeaderDescriptionComponentConfig?.height ??
                props.theme?.config?.subheaderDescription?.height
              }
              onChange={handleFieldChange('height')}
              options={{ suffix: 'px' }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
